/**
 * VenturaZ — lightweight i18n context.
 *
 * Progressive rollout: we support English (en) as the source of truth and
 * Hindi (hi) as a first non-English locale for the Indian market. Any string
 * key that isn't found in the active locale falls back to English so partial
 * translations don't break the UI.
 *
 * Usage:
 *   const { t, lang, setLang } = useI18n();
 *   <h1>{t("landing.headline")}</h1>
 *   <LanguageSwitcher />
 */
import React, { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  en: {
    "nav.for_startups": "For Startups",
    "nav.for_investors": "For Investors",
    "nav.marketplace": "Curated Deals",
    "nav.how_it_works": "How It Works",
    "nav.pricing": "Pricing",
    "nav.resources": "Resources",
    "nav.login": "Login",
    "nav.get_started": "Get Started",
    "landing.eyebrow": "VENTURAZ · VERIFIED CAPITAL-ACCESS PLATFORM",
    "landing.headline_1": "Where serious startups meet",
    "landing.headline_2": "curated capital.",
    "landing.subhead":
      "VenturaZ connects startups and businesses with relevant investors, lenders, family offices, funds, and institutional capital providers through verified company data, intelligent matching, capital-readiness scoring, diligence reports, and secure deal rooms.",
    "landing.cta_startup": "Apply for Funding →",
    "landing.cta_investor": "Join as Investor →",
    "landing.cta_explore": "Explore Verified Opportunities →",
    "landing.usp_kyc": "TRUST SCORE + HEALTH CHECK",
    "landing.usp_access": "CONTROLLED DATA ACCESS",
    "landing.usp_advisor": "DILIGENCE-BACKED DEAL ROOMS",
    "common.language": "Language",
    "common.english": "English",
    "common.hindi": "हिन्दी",
  },
  hi: {
    "nav.for_startups": "स्टार्टअप्स के लिए",
    "nav.for_investors": "निवेशकों के लिए",
    "nav.marketplace": "मार्केटप्लेस",
    "nav.how_it_works": "यह कैसे काम करता है",
    "nav.pricing": "प्राइसिंग",
    "nav.resources": "संसाधन",
    "nav.login": "लॉगिन",
    "nav.get_started": "शुरू करें",
    "landing.eyebrow": "कॉर्पज़ो एक्स · सत्यापित पूंजी-पहुँच प्लेटफ़ॉर्म",
    "landing.headline_1": "जहाँ गंभीर स्टार्टअप्स मिलते हैं",
    "landing.headline_2": "क्यूरेटेड कैपिटल से।",
    "landing.subhead":
      "कॉर्पज़ो एक्स स्टार्टअप्स और व्यवसायों को सत्यापित कंपनी डेटा, बुद्धिमान मिलान, पूंजी-तत्परता स्कोरिंग, डिलिजेंस रिपोर्ट्स और सुरक्षित डील रूम्स के माध्यम से प्रासंगिक निवेशकों, ऋणदाताओं, फैमिली ऑफिस, फंड्स और संस्थागत पूंजी प्रदाताओं से जोड़ता है।",
    "landing.cta_startup": "फंडिंग के लिए आवेदन करें →",
    "landing.cta_investor": "निवेशक बनें →",
    "landing.cta_explore": "सत्यापित अवसर देखें →",
    "landing.usp_kyc": "ट्रस्ट स्कोर + हेल्थ चेक",
    "landing.usp_access": "नियंत्रित डेटा एक्सेस",
    "landing.usp_advisor": "डिलिजेंस-आधारित डील रूम्स",
    "common.language": "भाषा",
    "common.english": "English",
    "common.hindi": "हिन्दी",
  },
};

const I18nContext = createContext(null);

const STORAGE_KEY = "venturaz_lang";

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "en"; } catch (_) { return "en"; }
  });

  const setLang = (next) => {
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* no-op */ }
  };

  useEffect(() => {
    try { document.documentElement.lang = lang; } catch (_) { /* no-op */ }
  }, [lang]);

  const t = (key) => {
    const dict = DICT[lang] || DICT.en;
    return dict[key] ?? DICT.en[key] ?? key;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t, available: Object.keys(DICT) }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Fallback for callers outside the provider — return English straight-through
    return { lang: "en", setLang: () => {}, t: (k) => (DICT.en[k] ?? k), available: ["en"] };
  }
  return ctx;
}

export function LanguageSwitcher({ className = "", size = "sm" }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div className={`inline-flex items-center gap-1 border border-navy/15 rounded-full px-1 py-0.5 ${className}`} data-testid="language-switcher">
      {[
        { code: "en", label: "EN" },
        { code: "hi", label: "हिं" },
      ].map(({ code, label }) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          className={`px-2 ${size === "sm" ? "py-0.5 text-[10px]" : "py-1 text-xs"} rounded-full font-heading uppercase tracking-wider transition-all ${
            lang === code ? "bg-navy text-white" : "text-ink-muted hover:text-ink"
          }`}
          data-testid={`language-switch-${code}`}
          aria-pressed={lang === code}
          aria-label={t("common.language") + " " + label}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
